import axios from 'axios';
import { encryptMessageBody, ensureMessengerCryptoReady, isE2EChatType } from '@/services/messenger-crypto.service';

export type MessengerDmPolicy = 'EVERYONE' | 'REQUESTS' | 'NOBODY';

export interface IMessengerUser {
  id: number;
  login: string;
  firstName?: string;
  lastName?: string;
  imageUrl?: string;
  authorities?: string[];
}

export interface IMessengerPrivacy {
  discoverable: boolean;
  dmPolicy: MessengerDmPolicy;
  showLastSeen: boolean;
  allowAddToGroups: boolean;
  onboardingSeen: boolean;
  blocked?: boolean;
  /** Есть ли зашифрованный бекап ключей на сервере. */
  keyBackupEnabled?: boolean;
}

export interface IMessengerKeyBackup {
  exists: boolean;
  updatedAt?: string;
  salt?: string;
  iv?: string;
  data?: string;
  kdf?: string;
  iterations?: number;
  version?: number;
}

export interface IMessengerAttachment {
  id: number;
  contentType: string;
  url: string;
  width?: number;
  height?: number;
}

export interface IMessengerReplyPreview {
  id: number;
  body?: string | null;
  author?: IMessengerUser | null;
  deleted?: boolean;
  hasAttachment?: boolean;
}

export interface IMessengerForwardPreview {
  id: number;
  chatId?: number;
  body?: string | null;
  author?: IMessengerUser | null;
  deleted?: boolean;
}

export interface IMessengerMessage {
  id: number;
  chatId: number;
  author?: IMessengerUser | null;
  body?: string | null;
  encryption?: 'NONE' | 'E2E' | string;
  ciphertext?: string | null;
  kind?: 'USER' | 'SYSTEM_EVENT' | string;
  systemEventType?: string | null;
  pinnedAt?: string | null;
  pinned?: boolean;
  createdDate?: string;
  editedAt?: string;
  deleted?: boolean;
  replyTo?: IMessengerReplyPreview | null;
  forwardedFrom?: IMessengerForwardPreview | null;
  attachments: IMessengerAttachment[];
  reactions: Record<string, number>;
}

export interface IMessengerReactionActor {
  reactionKey: string;
  user?: IMessengerUser | null;
}

export interface IMessengerChat {
  id: number;
  type: 'DIRECT' | 'SUPPORT' | 'GROUP' | 'PUBLIC' | 'SYSTEM';
  title: string;
  description?: string;
  forcedMembership?: boolean;
  /** Настройка чата: участники могут писать. */
  membersCanPost?: boolean;
  /** Для текущего пользователя — показывать композер. */
  canPost?: boolean;
  supportOwnerId?: number;
  /** SUPPORT: владелец в глобальном админ-блоке Liga.Chat. */
  supportOwnerBlocked?: boolean;
  lastMessage?: IMessengerMessage;
  unreadCount?: number;
  muted?: boolean;
  pinned?: boolean;
  pinnedAt?: string;
  myRole?: 'OWNER' | 'ADMIN' | 'MEMBER';
  canDeleteForEveryone?: boolean;
  peerLastReadAt?: string;
  /** Аватар собеседника / владельца support; иначе на клиенте дефолт. */
  avatarUrl?: string | null;
}

export interface IMessengerMember {
  userId: number;
  user?: IMessengerUser | null;
  role?: 'OWNER' | 'ADMIN' | 'MEMBER';
  lastReadAt?: string;
}

export interface IMessengerGlobalSearch {
  chats: IMessengerChat[];
  messages: IMessengerMessage[];
}

export interface IMessengerDmRequest {
  id: number;
  fromUser: IMessengerUser;
  status: 'PENDING' | 'ACCEPTED' | 'DECLINED';
  message?: string;
  createdDate?: string;
}

export interface IProfanityWord {
  id?: number;
  word: string;
}

const attachmentObjectUrlCache = new Map<number, string>();

export default class MessengerService {
  async getChats(): Promise<IMessengerChat[]> {
    const { data } = await axios.get<IMessengerChat[]>('api/messenger/chats');
    return data || [];
  }

  async getPublics(): Promise<IMessengerChat[]> {
    const { data } = await axios.get<IMessengerChat[]>('api/messenger/publics');
    return data || [];
  }

  async getPrivacy(): Promise<IMessengerPrivacy> {
    const { data } = await axios.get<IMessengerPrivacy>('api/messenger/privacy');
    return data;
  }

  async updatePrivacy(payload: IMessengerPrivacy): Promise<IMessengerPrivacy> {
    const { data } = await axios.put<IMessengerPrivacy>('api/messenger/privacy', payload);
    return data;
  }

  async getKeyBackup(): Promise<IMessengerKeyBackup> {
    const { data } = await axios.get<IMessengerKeyBackup>('api/messenger/key-backup');
    return data || { exists: false };
  }

  async putKeyBackup(payload: Omit<IMessengerKeyBackup, 'exists' | 'updatedAt'>): Promise<IMessengerKeyBackup> {
    const { data } = await axios.put<IMessengerKeyBackup>('api/messenger/key-backup', payload);
    return data;
  }

  async searchUsers(q: string, role?: string): Promise<IMessengerUser[]> {
    const params: { q: string; role?: string } = { q };
    if (role) params.role = role;
    const { data } = await axios.get<IMessengerUser[]>('api/messenger/users/search', { params });
    return data || [];
  }

  /** Роли с groupId > 0 — для фильтра участников группы. */
  async getRoleFilterOptions(): Promise<Array<{ name: string; description: string }>> {
    const { data } = await axios.get<Array<{ name: string; description?: string; groupId?: number }>>(
      'api/authorities-full'
    );
    return (data || [])
      .filter(item => item?.name)
      .map(item => ({
        name: item.name,
        description: (item.description || '').trim() || item.name.replace(/^ROLE_/, ''),
      }))
      .sort((a, b) => a.description.localeCompare(b.description, 'ru'));
  }

  async direct(userId: number, message?: string): Promise<IMessengerChat> {
    const { data } = await axios.post<IMessengerChat>('api/messenger/chats/direct', { userId, message });
    return data;
  }

  async support(): Promise<IMessengerChat> {
    const { data } = await axios.post<IMessengerChat>('api/messenger/chats/support');
    return data;
  }

  async getDmRequests(): Promise<IMessengerDmRequest[]> {
    const { data } = await axios.get<IMessengerDmRequest[]>('api/messenger/dm-requests');
    return data || [];
  }

  async acceptDmRequest(id: number): Promise<IMessengerChat> {
    const { data } = await axios.post<IMessengerChat>(`api/messenger/dm-requests/${id}/accept`);
    return data;
  }

  async declineDmRequest(id: number): Promise<void> {
    await axios.post(`api/messenger/dm-requests/${id}/decline`);
  }

  async createPublic(payload: {
    title: string;
    description?: string;
    forcedMembership?: boolean;
    membersCanPost?: boolean;
  }): Promise<IMessengerChat> {
    const { data } = await axios.post<IMessengerChat>('api/messenger/chats/publics', payload);
    return data;
  }

  async createGroup(payload: { title: string; description?: string; memberIds: number[] }): Promise<IMessengerChat> {
    const { data } = await axios.post<IMessengerChat>('api/messenger/chats/groups', payload);
    return data;
  }

  async join(chatId: number): Promise<IMessengerChat> {
    const { data } = await axios.post<IMessengerChat>(`api/messenger/chats/${chatId}/join`);
    return data;
  }

  async mute(chatId: number, preset: string): Promise<IMessengerChat> {
    const { data } = await axios.post<IMessengerChat>(`api/messenger/chats/${chatId}/mute`, { preset });
    return data;
  }

  async leave(chatId: number): Promise<void> {
    await axios.delete(`api/messenger/chats/${chatId}/leave`);
  }

  async updateChat(
    chatId: number,
    payload: { title?: string; description?: string; membersCanPost?: boolean }
  ): Promise<IMessengerChat> {
    const { data } = await axios.patch<IMessengerChat>(`api/messenger/chats/${chatId}`, payload);
    return data;
  }

  async getMembers(chatId: number): Promise<IMessengerMember[]> {
    const { data } = await axios.get<IMessengerMember[]>(`api/messenger/chats/${chatId}/members`);
    return data || [];
  }

  async addMember(chatId: number, userId: number): Promise<IMessengerMember> {
    const { data } = await axios.post<IMessengerMember>(`api/messenger/chats/${chatId}/members`, { userId });
    return data;
  }

  async removeMember(chatId: number, userId: number): Promise<void> {
    await axios.delete(`api/messenger/chats/${chatId}/members/${userId}`);
  }

  async setMemberRole(chatId: number, userId: number, role: 'ADMIN' | 'MEMBER'): Promise<IMessengerMember> {
    const { data } = await axios.put<IMessengerMember>(`api/messenger/chats/${chatId}/members/${userId}/role`, {
      role,
    });
    return data;
  }

  async typing(chatId: number, typing: boolean): Promise<void> {
    await axios.post(`api/messenger/chats/${chatId}/typing`, { typing });
  }

  async setPinned(chatId: number, pinned: boolean): Promise<IMessengerChat> {
    const { data } = await axios.post<IMessengerChat>(`api/messenger/chats/${chatId}/pin`, { pinned });
    return data;
  }

  async deleteChat(chatId: number, forEveryone = false): Promise<void> {
    await axios.delete(`api/messenger/chats/${chatId}`, { params: { forEveryone } });
  }

  async markRead(chatId: number): Promise<void> {
    await axios.post(`api/messenger/chats/${chatId}/read`);
  }

  async getMessages(
    chatId: number,
    params?: { beforeId?: number; aroundId?: number; limit?: number }
  ): Promise<IMessengerMessage[]> {
    const { data } = await axios.get<IMessengerMessage[]>(`api/messenger/chats/${chatId}/messages`, {
      params: {
        beforeId: params?.beforeId,
        aroundId: params?.aroundId,
        limit: params?.limit,
      },
    });
    return data || [];
  }

  async searchMessages(chatId: number, query: string, limit = 30): Promise<IMessengerMessage[]> {
    const { data } = await axios.get<IMessengerMessage[]>(`api/messenger/chats/${chatId}/messages/search`, {
      params: { q: query, limit },
    });
    return data || [];
  }

  async globalSearch(query: string, limit = 20): Promise<IMessengerGlobalSearch> {
    const { data } = await axios.get<IMessengerGlobalSearch>('api/messenger/search', {
      params: { q: query, limit },
    });
    return data || { chats: [], messages: [] };
  }

  async getPinnedMessages(chatId: number): Promise<IMessengerMessage[]> {
    const { data } = await axios.get<IMessengerMessage[]>(`api/messenger/chats/${chatId}/pinned-messages`);
    return data || [];
  }

  async pinMessage(messageId: number, pinned: boolean): Promise<IMessengerMessage> {
    const { data } = await axios.post<IMessengerMessage>(`api/messenger/messages/${messageId}/pin`, { pinned });
    return data;
  }

  async forwardMessage(
    messageId: number,
    chatId: number,
    payload?: { body?: string; encryption?: string; ciphertext?: string }
  ): Promise<IMessengerMessage> {
    const { data } = await axios.post<IMessengerMessage>(`api/messenger/messages/${messageId}/forward`, {
      chatId,
      body: payload?.body,
      encryption: payload?.encryption,
      ciphertext: payload?.ciphertext,
    });
    return data;
  }

  async getReactionActors(messageId: number): Promise<IMessengerReactionActor[]> {
    const { data } = await axios.get<IMessengerReactionActor[]>(`api/messenger/messages/${messageId}/reactions`);
    return data || [];
  }

  async peerBlock(userId: number, reason?: string): Promise<void> {
    await axios.post('api/messenger/peer-block', { userId, reason });
  }

  async reportUser(payload: { userId: number; messageId?: number; chatId?: number; reason?: string }): Promise<void> {
    await axios.post('api/messenger/report', payload);
  }

  async sendMessage(
    chatId: number,
    body: string,
    image?: File,
    onUploadProgress?: (percent: number) => void,
    replyToId?: number,
    chatType?: IMessengerChat['type'],
    precomputed?: { encryption: string; ciphertext: string }
  ): Promise<IMessengerMessage> {
    let encryption = precomputed?.encryption;
    let ciphertext = precomputed?.ciphertext;
    let plainBody = body;
    if (!precomputed && isE2EChatType(chatType) && (body || '').trim()) {
      await ensureMessengerCryptoReady();
      encryption = 'E2E';
      ciphertext = await encryptMessageBody(chatId, body);
      plainBody = '';
    } else if (precomputed) {
      plainBody = '';
    }
    if (image) {
      const form = new FormData();
      form.append('body', plainBody || '');
      form.append('image', image);
      if (replyToId) {
        form.append('replyToId', String(replyToId));
      }
      if (encryption) {
        form.append('encryption', encryption);
        form.append('ciphertext', ciphertext || '');
      }
      const { data } = await axios.post<IMessengerMessage>(`api/messenger/chats/${chatId}/messages`, form, {
        onUploadProgress: event => {
          if (!onUploadProgress || !event.total) return;
          onUploadProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)));
        },
      });
      return data;
    }
    const { data } = await axios.post<IMessengerMessage>(`api/messenger/chats/${chatId}/messages`, {
      body: plainBody,
      replyToId: replyToId || undefined,
      encryption,
      ciphertext,
    });
    return data;
  }

  async updateMessage(messageId: number, body: string): Promise<IMessengerMessage> {
    const { data } = await axios.put<IMessengerMessage>(`api/messenger/messages/${messageId}`, { body });
    return data;
  }

  async deleteMessage(messageId: number): Promise<void> {
    await axios.delete(`api/messenger/messages/${messageId}`);
  }

  async toggleReaction(messageId: number, reactionKey: string): Promise<IMessengerMessage> {
    const { data } = await axios.post<IMessengerMessage>(`api/messenger/messages/${messageId}/reactions/${reactionKey}`);
    return data;
  }

  /** Загрузка вложения с JWT: обычный img src без Authorization даёт 401. */
  async getAttachmentObjectUrl(attachmentId: number): Promise<string> {
    const cached = attachmentObjectUrlCache.get(attachmentId);
    if (cached) return cached;
    const { data, headers } = await axios.get<Blob>(`api/messenger/attachments/${attachmentId}`, {
      responseType: 'blob',
    });
    const contentType = String(headers['content-type'] || data.type || '');
    if (contentType.includes('application/json') || contentType.includes('text/')) {
      throw new Error('attachment-not-ready');
    }
    const objectUrl = URL.createObjectURL(data);
    attachmentObjectUrlCache.set(attachmentId, objectUrl);
    return objectUrl;
  }

  async adminSupportChats(): Promise<IMessengerChat[]> {
    const { data } = await axios.get<IMessengerChat[]>('api/admin/messenger/support-chats');
    return data || [];
  }

  async adminBlockUser(userId: number, reason?: string): Promise<void> {
    const body: { reason?: string } = {};
    if (reason && reason.trim()) body.reason = reason.trim();
    await axios.post(`api/admin/messenger/users/${userId}/block`, body);
  }

  async adminUnblockUser(userId: number): Promise<void> {
    await axios.delete(`api/admin/messenger/users/${userId}/block`);
  }

  async listProfanityWords(): Promise<IProfanityWord[]> {
    const { data } = await axios.get<IProfanityWord[]>('api/admin/messenger/profanity-words');
    return data || [];
  }

  async addProfanityWord(word: string): Promise<IProfanityWord> {
    const { data } = await axios.post<IProfanityWord>('api/admin/messenger/profanity-words', { word });
    return data;
  }

  async deleteProfanityWord(id: number): Promise<void> {
    await axios.delete(`api/admin/messenger/profanity-words/${id}`);
  }
}
